import { useState } from 'react';
import { fetchRawMcpTools, McpTool, McpToolCategory, McpToolParameter } from '../api/mcpToolsApi';

export type { McpToolCategory };

const TOOL_CATEGORY_MAP: Record<string, string> = {
  verify_coverage: 'Coverages',
  get_coverage_detail: 'Coverages',
  delete_coverage: 'Coverages',
  find_claim_status: 'Claim Status',
  get_claim_status_detail: 'Claim Status',
  estimate_professional_care_cost: 'Patient Cost Estimator',
  get_professional_care_cost: 'Patient Cost Estimator',
};

const CATEGORY_ORDER = ['Coverages', 'Claim Status', 'Patient Cost Estimator'];

const toCamelCase = (str: string) =>
  str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const parseParam = (param: McpToolParameter): McpToolParameter => ({
  ...param,
  name: toCamelCase(param.name),
  properties: param.properties?.map((p) => parseParam(p)),
});

const groupIntoCategories = (rawTools: McpTool[]): McpToolCategory[] => {
  const categoryMap = rawTools.reduce<Record<string, McpTool[]>>((acc, { parameters, ...tool }) => {
    const cat = TOOL_CATEGORY_MAP[tool.name];
    if (!cat) return acc;
    const parsed: McpToolParameter[] = (parameters ?? []).map((param) => parseParam(param));
    (acc[cat] ??= []).push({ ...tool, parameters: parsed });
    return acc;
  }, {});

  return CATEGORY_ORDER
    .filter((cat) => cat in categoryMap)
    .map((category) => ({
      category,
      id: `mcp-tools-${category.toLowerCase().replace(/\s+/g, '-')}`,
      tools: categoryMap[category],
    }));
};

let cachedTools: McpToolCategory[] | null = null;
let cachedPromise: Promise<McpToolCategory[]> | null = null;

const loadTools = (): Promise<McpToolCategory[]> => {
  if (cachedTools) return Promise.resolve(cachedTools);
  if (!cachedPromise) {
    cachedPromise = fetchRawMcpTools()
      .then((data) => {
        cachedTools = groupIntoCategories(data);
        return cachedTools;
      })
      .catch((error) => {
        cachedPromise = null;
        throw error;
      });
  }
  return cachedPromise;
};

loadTools().catch(Function.prototype as () => void);

const useMcpTools = () => {
  const [tools, setTools] = useState<McpToolCategory[]>(cachedTools ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = () => {
    if (cachedTools) {
      setTools(cachedTools);
      return;
    }
    setLoading(true);
    loadTools()
      .then((data) => {
        setTools(data);
        setLoading(false);
        return data;
      })
      .catch(() => {
        setError('Failed to load MCP tools');
        setLoading(false);
      });
  };

  return { tools, loading, error, fetch };
};

export default useMcpTools;
